import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
} from "react-hook-form";

import type { UpdatePasswordInput } from "../../api/edit-password";
import {
  updatePasswordSchemaInput,
  verifyPassword,
} from "../../api/edit-password";
import HidenTextField from "../../components/HidenTextField";
import Loading from "../../components/Loading";
import RegisterPasswordTextField from "../../components/RegisterPasswordTextField";
import { notify } from "../../pages/public/components/Toast";

function Component(): React.JSX.Element {
  const { t } = useTranslation();
  const [notViewHelperText, setNotViewHelperText] = useState(true);

  const methods = useForm<UpdatePasswordInput>({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    resolver: yupResolver(updatePasswordSchemaInput),
    criteriaMode: "all",
    mode: "all",
  });

  const { control, handleSubmit, formState, setError, resetField } = methods;

  const mutation = useMutation({
    mutationFn: async (data: UpdatePasswordInput) => {
      // TODO: Implement password update API call
      console.log("Password update data:", data);
      return Promise.resolve();
    },
    onSuccess: () => {
      notify(t("editPassword.successMessage"), "success", "");
    },
    onError: () => {
      notify(t("editPassword.errorMessage"), "error", "");
    },
  });

  const onSubmit: SubmitHandler<UpdatePasswordInput> = (data): void => {
    mutation.mutate(data);
  };

  const onBlurApiField = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): Promise<void> => {
    const { value } = e.target;

    if (!value) {
      setError("currentPassword", {
        message: t("validation.passwordRequiredMasc"),
      });
      return;
    }

    try {
      if (value) {
        const { data: resultVerifyPassword, status } =
          await verifyPassword(value);

        if (!resultVerifyPassword.valid) {
          setError("currentPassword", {
            message: t("editPassword.incorrectPassword"),
          });
        }

        if (status !== 200) {
          const message = t("register.serviceUnavailable");
          setError("currentPassword", {
            message,
          });
          notify(message, "info", "");
        }
      } else {
        resetField("currentPassword", {
          keepError: false,
        });
      }
    } catch (error) {
      const message = t("register.serviceUnavailable");
      setError("currentPassword", {
        message,
      });
      notify(message, "info", "");
    }
  };

  return (
    <section>
      <FormProvider {...methods}>
        <form
          autoComplete="off"
          autoSave="off"
          onSubmit={(e) => void handleSubmit(onSubmit)(e)}
        >
          <Stack gap={3}>
            <Stack gap={1}>
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-start",
                }}
              >
                <Typography
                  sx={{
                    color: "white.light",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    marginRight: "10px",
                  }}
                  variant="body1"
                >
                  {t("editPassword.breadcrumb")}
                </Typography>
                <ChevronRightIcon
                  fontSize="small"
                  sx={{ color: "white.light" }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: {
                    xs: "24px",
                    sm: "24px",
                    md: "32px",
                    lg: "32px",
                    xl: "32px",
                  },
                  fontWeight: 500,
                }}
                textAlign="left"
              >
                {t("editPassword.title")}
              </Typography>
            </Stack>
            <Controller
              control={control}
              name="currentPassword"
              render={({ field, fieldState: { error } }) => (
                <HidenTextField
                  {...field}
                  error={Boolean(error)}
                  fullWidth
                  helperText={error ? error.message : undefined}
                  label={t("editPassword.currentPasswordLabel")}
                  onBlur={(e) => void onBlurApiField(e)}
                  variant="outlined"
                />
              )}
            />
            <Controller
              control={control}
              name="newPassword"
              render={({ field, fieldState: { error } }) => {
                const matches = (() => {
                  const result =
                    error && error.types
                      ? (error.types.matches as string[])
                      : [];
                  if (!Array.isArray(result)) return [result];
                  return result;
                })();

                return (
                  <RegisterPasswordTextField
                    {...field}
                    digitsError={matches.some((item) => item === "digitsError")}
                    error={Boolean(error)}
                    fullWidth
                    label={t("editPassword.passwordLabel")}
                    lowercaseLetterError={matches.some(
                      (item) => item === "lowercaseLetterError",
                    )}
                    minMaxError={matches.some((item) => item === "minMaxError")}
                    notViewHelperText={notViewHelperText}
                    onFocus={() => {
                      setNotViewHelperText(false);
                    }}
                    specialCharError={matches.some(
                      (item) => item === "specialCharError",
                    )}
                    uppercaseLetterError={matches.some(
                      (item) => item === "uppercaseLetterError",
                    )}
                    variant="outlined"
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="confirmPassword"
              render={({ field, fieldState: { error } }) => (
                <HidenTextField
                  {...field}
                  error={Boolean(error)}
                  fullWidth
                  helperText={error?.message || " "}
                  label={t("editPassword.confirmPasswordLabel")}
                  variant="outlined"
                />
              )}
            />
            <Box
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Button
                color="primary"
                component={Link}
                size="medium"
                to="/my-account"
                variant="outlined"
              >
                {t("editPassword.cancelButton")}
              </Button>
              <Button
                color="primary"
                disabled={!formState.isValid || mutation.isPending}
                size="medium"
                type="submit"
                variant="contained"
              >
                {mutation.isPending ? t("editPassword.saving") : t("editPassword.saveButton")}
              </Button>
            </Box>
          </Stack>
        </form>
      </FormProvider>
    </section>
  );
}

export const Route = createFileRoute("/__auth/edit-password")({
  component: Component,
  pendingComponent: Loading,
});
